import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { getPhoneNumberError, toE164 } from "components/PhoneNumberField";
import { useWriterComponentState } from "hooks/useWriterComponentState";
import { validateEmail } from "utils";

import { EmailStep } from "./components/EmailStep";
import { ExternalFormStep } from "./components/ExternalFormStep";
import { ClientAppEvent } from "../../../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  onNotification: (message: string) => void;
}

interface CheckEmailStep {
  email: string;
}

interface ExternalInviteStep {
  firstName: string;
  lastName: string;
  companyName: string;
  phoneNumber: string;
}

type ModalStep = "email" | "external-form";

interface ShipperExistsResponse {
  exists: boolean;
}

export function AddSupplierModal(props: Props) {
  const { open, onClose, onNotification } = props;
  const posthog = usePostHog();

  const [modalStep, setModalStep] = useState<ModalStep>("email");
  const [emailData, setEmailData] = useState<CheckEmailStep>({ email: "" });
  const [externalData, setExternalData] = useState<ExternalInviteStep>({
    firstName: "",
    lastName: "",
    companyName: "",
    phoneNumber: "",
  });
  const [emailValidationError, setEmailValidationError] = useState<string>("");
  const [externalValidationErrors, setExternalValidationErrors] = useState<
    Partial<ExternalInviteStep>
  >({});

  // Email check state
  const {
    loading: checkingEmail,
    error: emailCheckError,
    write: checkEmail,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/shipper-exists",
      body: { email: emailData.email.toLowerCase() },
    },
    errorMessage: "Failed to check email",
  });

  // Internal invite state
  const {
    loading: sendingInternalInvite,
    error: internalInviteError,
    write: sendInternalInvite,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/supplier-network-internal-invites",
      body: { email: emailData.email.toLowerCase() },
    },
    errorMessage: "Failed to send internal invite",
  });

  // External invite state
  const {
    loading: sendingExternalInvite,
    error: externalInviteError,
    write: sendExternalInvite,
    resetState: resetExternalInviteState,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/supplier-network-external-invites",
      body: {
        first_name: externalData.firstName,
        last_name: externalData.lastName,
        email: emailData.email.toLowerCase(),
        company_name: externalData.companyName,
        ...(externalData.phoneNumber && {
          phone_number: toE164(externalData.phoneNumber),
        }),
      },
    },
    errorMessage: "Failed to send external invite",
  });

  const handleClose = () => {
    setModalStep("email");
    setEmailData({ email: "" });
    setExternalData({
      firstName: "",
      lastName: "",
      companyName: "",
      phoneNumber: "",
    });
    setEmailValidationError("");
    setExternalValidationErrors({});
    onClose();
  };

  const handleInternalInviteSubmit = () => {
    sendInternalInvite(() => {
      posthog?.capture(ClientAppEvent.SUPPLIER_INVITE_SENT_INTERNAL);
      onNotification("Invite sent successfully!");
      handleClose();
    });
  };

  const handleEmailSubmit = () => {
    const emailError = validateEmail(emailData.email);
    if (emailError) {
      setEmailValidationError(emailError);
      return;
    }

    setEmailValidationError("");
    setExternalValidationErrors({});
    resetExternalInviteState();
    checkEmail<ShipperExistsResponse>((response) => {
      if (response.exists) {
        handleInternalInviteSubmit();
      } else {
        setModalStep("external-form");
      }
    });
  };

  const validateExternalForm = (): Partial<ExternalInviteStep> => {
    const errors: Partial<ExternalInviteStep> = {};

    if (!externalData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!externalData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!externalData.companyName.trim()) {
      errors.companyName = "Company name is required";
    }

    // Phone is optional; validate only when provided so an incomplete or
    // malformed number is caught before the invite is sent.
    const phoneNumberError = getPhoneNumberError(externalData.phoneNumber);
    if (phoneNumberError) {
      errors.phoneNumber = phoneNumberError;
    }

    return errors;
  };

  const handleExternalFormSubmit = () => {
    const errors = validateExternalForm();
    setExternalValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      sendExternalInvite(() => {
        posthog?.capture(ClientAppEvent.SUPPLIER_INVITE_SENT_EXTERNAL);
        onNotification("Invite sent successfully!");
        handleClose();
      });
    }
  };

  const handleBackToEmail = () => {
    setModalStep("email");
    setExternalValidationErrors({});
  };

  const isEmailStepValid =
    emailData.email.trim() !== "" && !emailValidationError;
  const isExternalFormValid = Object.keys(validateExternalForm()).length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h5">
          {modalStep === "email" && "Add Supplier"}
          {modalStep === "external-form" && "Supplier Details"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {modalStep === "email" && (
          <EmailStep
            email={emailData.email}
            onEmailChange={(email) => setEmailData({ email })}
            emailValidationError={emailValidationError}
            emailCheckError={emailCheckError}
            internalInviteError={internalInviteError}
            onEmailValidationErrorClear={() => setEmailValidationError("")}
          />
        )}

        {modalStep === "external-form" && (
          <ExternalFormStep
            email={emailData.email}
            externalData={externalData}
            onExternalDataChange={setExternalData}
            externalValidationErrors={externalValidationErrors}
            onValidationErrorClear={(field) => {
              setExternalValidationErrors({
                ...externalValidationErrors,
                [field]: undefined,
              });
            }}
            externalInviteError={externalInviteError}
          />
        )}
      </DialogContent>
      <DialogActions>
        {modalStep !== "email" && (
          <Button
            onClick={handleBackToEmail}
            disabled={
              checkingEmail || sendingInternalInvite || sendingExternalInvite
            }
          >
            Back
          </Button>
        )}
        {modalStep === "email" && (
          <Button
            onClick={handleClose}
            disabled={
              checkingEmail || sendingInternalInvite || sendingExternalInvite
            }
          >
            Cancel
          </Button>
        )}

        {modalStep === "email" && (
          <LoadingWrappedNode loading={checkingEmail}>
            <Button
              variant="contained"
              onClick={handleEmailSubmit}
              disabled={!isEmailStepValid || checkingEmail}
            >
              Continue
            </Button>
          </LoadingWrappedNode>
        )}

        {modalStep === "external-form" && (
          <LoadingWrappedNode loading={sendingExternalInvite}>
            <Button
              variant="contained"
              onClick={handleExternalFormSubmit}
              disabled={!isExternalFormValid || sendingExternalInvite}
            >
              Send Invite
            </Button>
          </LoadingWrappedNode>
        )}
      </DialogActions>
    </Dialog>
  );
}
